/**
 * Bookmarklet source — pure DOM JavaScript that injects the branded
 * Allstar iframe into the current page AFTER a user-picked element.
 * Intended for hltv.org match coverage / event / news pages.
 *
 * Kept as a string constant so the install page can:
 *   1. Render it as a `javascript:` href the user drags to their
 *      bookmarks bar.
 *   2. Display it raw in a copy-paste box for users who can't drag.
 *
 * Runs in the host page's context — no React, no styled-components,
 * everything is plain DOM.
 *
 * Behavior:
 *   1. First click of the bookmark enters PICK mode. A green dashed
 *      outline tracks the cursor over hoverable elements. Click any
 *      element on the page to INSERT the Allstar iframe immediately
 *      after it (sibling, not replacement) with vertical padding so the
 *      iframe doesn't crowd the surrounding content. Esc cancels.
 *   2. The picked element is left untouched — visible and in flow — so
 *      the demo reads as "Allstar slotted into the page" rather than
 *      "Allstar replaced something". Teardown just removes the iframe.
 *   3. A floating geo chip stays in the upper-right with a sponsor
 *      switcher and "Pick different element" / "Close" buttons.
 *   4. Re-clicking the bookmark tears everything down (removes iframe
 *      + chip; nothing else to restore since we never modified the
 *      host page).
 *
 * IMPORTANT: no `//` line comments allowed inside the template literal
 * below — the `replace(/\n/g, "")` collapses to a single line and `//`
 * would swallow the rest. For inline notes inside the body, use the
 * slash-star / star-slash block-comment form.
 */

import { SUPPORTED_GEOS } from "../EmbeddablePlaylist/Components/BrandedEmbeddablePlaylist/supportedGeos";

interface BuildOpts {
  /** Origin to load the iframe from. e.g. https://allstar.gg */
  base: string;
  /** Playlist ID to embed. */
  playlistId: string;
  /** Default geo to start with. */
  defaultGeo?: string;
}

/* Bookmarklet body keeps the legacy {c,l} key shape for the geo entries
   (short keys to keep the bookmarklet URL small once minified). Map the
   shared catalog into that shape at build time. */
const buildGeoLiteralForBookmarklet = (): string =>
  JSON.stringify(SUPPORTED_GEOS.map((g) => ({ c: g.code, l: g.label })));

/**
 * Returns the bookmarklet body as a single-line `javascript:` URL,
 * suitable for use as an <a href="..."> the user drags to their
 * bookmarks bar.
 */
export const buildBookmarkletHref = (opts: BuildOpts): string => {
  const body = buildBookmarkletBody(opts);
  return "javascript:" + encodeURIComponent(body);
};

/**
 * Returns the unencoded JS body for display in a copy-paste box.
 */
export const buildBookmarkletBody = ({
  base,
  playlistId,
  defaultGeo = "DE",
}: BuildOpts): string => {
  // The bookmarklet body. Wrapped in an IIFE so it leaves no globals
  // beyond `__allstarHLTVDemo` (used to detect re-clicks → teardown).
  // Template-literal placeholders are escaped via JSON.stringify on the
  // string-typed args so user-supplied values can't break out.
  const BASE_LIT = JSON.stringify(base);
  const PLAYLIST_LIT = JSON.stringify(playlistId);
  const GEO_LIT = JSON.stringify(defaultGeo);
  const GEOS_LIT = buildGeoLiteralForBookmarklet();

  return `(function(){
var W=window;
if(W.__allstarHLTVDemo){W.__allstarHLTVDemo.teardown();return;}
var BASE=${BASE_LIT};
var PLAYLIST=${PLAYLIST_LIT};
/* Host-page override: if the page the bookmarklet runs on has a
   ?playlist=... query param, prefer that over the baked-in default.
   Lets a demo navigate to https://hltv.org/...?playlist=<id> and have
   the bookmarklet pick up that playlist without rebuilding the
   bookmark. Wrapped in try/catch so a malformed location.search never
   breaks the bookmarklet. */
try{var _qs=W.location&&W.location.search;if(_qs){var _q=new URLSearchParams(_qs).get("playlist");if(_q)PLAYLIST=_q;}}catch(_e){}
/* Supported geos injected from the shared catalog
   (supportedGeos.ts) so the bookmarklet and the hosted /iframe-demo
   page stay in lockstep — adding a country in one place updates both
   surfaces. Entries use short {c,l} keys to keep the minified
   bookmarklet URL compact. */
var GEOS=${GEOS_LIT};
var geo=${GEO_LIT};
/* Size presets match the two embed contexts HLTV ships and stay in
   lockstep with /iframe-demo's HLTV sizes array. Desktop is the
   638x226 in-article sliver; mobile is the 370x320 stacked card. The
   bookmarklet defaults to desktop on initial placement and exposes
   both as one-click snap buttons in the floating chip. */
var PRESET_DESKTOP={w:638,h:226};
var PRESET_MOBILE={w:370,h:320};
var src=function(){return BASE+"/iframe?platform=HLTV&playlist="+PLAYLIST+"&branded=1&geo="+geo;};
var state={iframe:null,target:null,picking:false,hovered:null,highlight:null,w:PRESET_DESKTOP.w,h:PRESET_DESKTOP.h,minimized:false};
var chip=document.createElement("div");
chip.setAttribute("data-allstar-demo","");
document.body.appendChild(chip);
/* Compact mode kicks in when the host page's viewport is mobile-sized
   (e.g. Chrome devtools device emulation, narrow window). The chip
   shrinks its width, drops the verbose help copy, stacks the preset
   buttons more tightly, and inset offsets shrink — so it doesn't
   cover half a 360px-wide emulated viewport. */
var isCompact=function(){return W.innerWidth<560;};
var applyChipShell=function(){
var compact=isCompact();
if(state.minimized){
/* Minimized: tiny circular pill with just the Allstar mark. Click
   anywhere on it to restore. Tighter offsets so it tucks into the
   corner like a chat-launcher bubble. */
chip.style.cssText="position:fixed;top:"+(compact?"8px":"16px")+";right:"+(compact?"8px":"16px")+";z-index:2147483647;width:36px;height:36px;padding:0;background:rgba(12,12,16,0.94);border:1px solid rgba(77,189,148,0.45);border-radius:999px;color:#fff;font-family:Inter,system-ui,-apple-system,sans-serif;font-size:13px;font-weight:700;display:flex;align-items:center;justify-content:center;cursor:pointer;box-shadow:0 8px 20px rgba(0,0,0,0.45);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);";
return;
}
chip.style.cssText="position:fixed;top:"+(compact?"8px":"16px")+";right:"+(compact?"8px":"16px")+";z-index:2147483647;min-width:"+(compact?"180px":"240px")+";max-width:"+(compact?"calc(100vw - 16px)":"none")+";padding:"+(compact?"10px 12px":"14px 16px")+";background:rgba(12,12,16,0.94);border:1px solid rgba(77,189,148,0.45);border-radius:"+(compact?"10px":"12px")+";color:#fff;font-family:Inter,system-ui,-apple-system,sans-serif;font-size:"+(compact?"12px":"13px")+";box-shadow:0 12px 36px rgba(0,0,0,0.45);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);cursor:auto;";
};
applyChipShell();
/* One chip-level click handler. When the chip is in its minimized
   pill state, clicking anywhere on the bubble restores it to the full
   panel. When expanded, this is a no-op — interior buttons handle
   their own clicks and stopPropagation where needed. */
chip.addEventListener("click",function(){if(state.minimized){state.minimized=false;renderChip();}});
var renderChip=function(){
applyChipShell();
var compact=isCompact();
if(state.minimized){
/* Minimized body: just a green "A" mark, with hover-friendly tooltip
   via aria-label. Whole chip is clickable to restore (via the
   chip-level listener below). */
chip.innerHTML="<span aria-label='Restore Allstar demo controls' style='color:#4dbd94;font-weight:800;letter-spacing:0.5px;'>A</span>";
return;
}
if(state.picking){
var pickCopy=compact?"Click any element to drop the iframe after it. Esc to cancel.":"Hover any element. Click to drop the Allstar iframe immediately after it — defaults to the Desktop preset ("+PRESET_DESKTOP.w+"×"+PRESET_DESKTOP.h+"), switch to Mobile or a custom size from the chip after placement. Esc to cancel.";
chip.innerHTML="<div style='font-size:"+(compact?"9px":"10px")+";font-weight:700;letter-spacing:1px;text-transform:uppercase;color:#4dbd94;margin-bottom:4px;'>Pick mode</div><div style='font-size:"+(compact?"12px":"14px")+";font-weight:700;letter-spacing:0.4px;margin-bottom:"+(compact?"6px":"8px")+";'>Click an element to insert after</div><div style='font-size:"+(compact?"11px":"12px")+";color:rgba(255,255,255,0.65);line-height:1.45;margin-bottom:"+(compact?"8px":"10px")+";'>"+pickCopy+"</div><button id='_ascancel' style='width:100%;padding:"+(compact?"6px 10px":"8px 10px")+";background:transparent;border:1px solid rgba(255,255,255,0.18);border-radius:6px;color:rgba(255,255,255,0.7);font:inherit;font-size:"+(compact?"11px":"12px")+";cursor:pointer;'>Cancel (Esc)</button>";
var c=chip.querySelector("#_ascancel");if(c)c.addEventListener("click",teardown);
return;
}
/* Combobox: typeable input backed by a <datalist> so reviewers can
   either pick from the supported list or type any ISO-3166 alpha-2
   code (the iframe's resolver accepts arbitrary strings — unknown
   codes simply fall through to the Allstar self-takeover). Storing
   the typed value uppercased so case doesn't matter. */
var opts=GEOS.map(function(g){return "<option value='"+g.c+"'>"+g.l+"</option>";}).join("");
var inputStyle="width:100%;padding:"+(compact?"5px 7px":"6px 8px")+";background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.15);border-radius:6px;color:#fff;font-family:ui-monospace,Menlo,monospace;font-size:"+(compact?"11px":"12px")+";box-sizing:border-box;";
var labelStyle="font-size:"+(compact?"9px":"10px")+";font-weight:700;letter-spacing:0.6px;text-transform:uppercase;color:rgba(255,255,255,0.5);margin-bottom:4px;";
var presetBtnStyle="flex:1;padding:"+(compact?"5px 6px":"6px 8px")+";background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.18);border-radius:6px;color:rgba(255,255,255,0.85);font:inherit;font-size:"+(compact?"10px":"11px")+";font-weight:600;cursor:pointer;";
var activePresetStyle="flex:1;padding:"+(compact?"5px 6px":"6px 8px")+";background:rgba(77,189,148,0.18);border:1px solid rgba(77,189,148,0.5);border-radius:6px;color:#fff;font:inherit;font-size:"+(compact?"10px":"11px")+";font-weight:600;cursor:pointer;";
var actionBtnStyle="padding:"+(compact?"5px 8px":"6px 10px")+";background:transparent;border:1px solid rgba(255,255,255,0.18);border-radius:6px;color:rgba(255,255,255,0.7);font:inherit;font-size:"+(compact?"10px":"11px")+";cursor:pointer;";
var pickBtnStyle="padding:"+(compact?"5px 8px":"6px 10px")+";background:rgba(77,189,148,0.15);border:1px solid rgba(77,189,148,0.35);border-radius:6px;color:#fff;font:inherit;font-size:"+(compact?"10px":"11px")+";cursor:pointer;";
var isDesktop=state.w===PRESET_DESKTOP.w&&state.h===PRESET_DESKTOP.h;
var isMobile=state.w===PRESET_MOBILE.w&&state.h===PRESET_MOBILE.h;
/* Compact labels lose the dimension suffix to save horizontal real
   estate; full mode keeps "Desktop 638×226" / "Mobile 370×320" as a
   visible reminder. */
var deskLabel=compact?"Desktop":"Desktop 638×226";
var mobLabel=compact?"Mobile":"Mobile 370×320";
var headerLabel=compact?"Allstar":"Allstar demo";
var geoLabel=compact?"Geo":"Viewer geo";
chip.innerHTML="<div style='display:flex;align-items:center;justify-content:space-between;margin-bottom:4px;'><div style='font-size:"+(compact?"9px":"10px")+";font-weight:700;letter-spacing:1px;text-transform:uppercase;color:rgba(255,255,255,0.55);'>"+headerLabel+"</div><button id='_asmin' aria-label='Minimize' title='Minimize' style='width:20px;height:20px;padding:0;background:transparent;border:1px solid rgba(255,255,255,0.18);border-radius:4px;color:rgba(255,255,255,0.7);font:inherit;font-size:14px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;'>−</button></div><div style='font-size:"+(compact?"12px":"14px")+";font-weight:700;letter-spacing:0.4px;margin-bottom:"+(compact?"6px":"8px")+";'>"+geoLabel+"</div><input id='_asgeo' list='_asgeolist' value='"+geo+"' autocomplete='off' spellcheck='false' placeholder='Type or pick (e.g. US)' style='width:100%;padding:"+(compact?"6px 8px":"8px 10px")+";background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.18);border-radius:8px;color:#fff;font:inherit;font-weight:600;font-family:ui-monospace,Menlo,monospace;text-transform:uppercase;letter-spacing:1px;font-size:"+(compact?"12px":"inherit")+";box-sizing:border-box;'><datalist id='_asgeolist'>"+opts+"</datalist><div id='_asstatus' style='margin-top:"+(compact?"8px":"10px")+";font-size:"+(compact?"11px":"12px")+";display:flex;align-items:center;gap:8px;'></div><div style='border-top:1px solid rgba(255,255,255,0.08);margin-top:"+(compact?"10px":"12px")+";padding-top:"+(compact?"10px":"12px")+";'><div style='"+labelStyle+"margin-bottom:6px;'>Size preset</div><div style='display:flex;gap:6px;margin-bottom:"+(compact?"8px":"10px")+";'><button id='_asdesk' style='"+(isDesktop?activePresetStyle:presetBtnStyle)+"'>"+deskLabel+"</button><button id='_asmob' style='"+(isMobile?activePresetStyle:presetBtnStyle)+"'>"+mobLabel+"</button></div><div style='display:flex;gap:8px;'><div style='flex:1;'><div style='"+labelStyle+"'>W</div><input id='_asw' type='number' min='80' step='10' value='"+state.w+"' style='"+inputStyle+"'></div><div style='flex:1;'><div style='"+labelStyle+"'>H</div><input id='_ash' type='number' min='80' step='10' value='"+state.h+"' style='"+inputStyle+"'></div></div></div><div style='margin-top:"+(compact?"8px":"10px")+";display:flex;flex-direction:column;gap:6px;'><button id='_asfit' style='"+actionBtnStyle+"'>"+(compact?"Fit element":"Reset to element width")+"</button><button id='_aspick' style='"+pickBtnStyle+"'>"+(compact?"Pick element":"Pick different element")+"</button><button id='_asclose' style='"+actionBtnStyle+"'>Close</button></div>";
var minBtn=chip.querySelector("#_asmin");
if(minBtn)minBtn.addEventListener("click",function(e){e.stopPropagation();state.minimized=true;renderChip();});
var sel=chip.querySelector("#_asgeo");
/* Commit the typed/picked geo on the change event (blur/Enter/
   datalist-pick). We don't reload the iframe on every keystroke —
   that would thrash src reloads while the user types "U" then "US".
   The change event fires once when the field's value is finalized.
   Normalize to uppercase so "us" and "US" behave the same. */
if(sel)sel.addEventListener("change",function(e){var v=(e.target.value||"").trim().toUpperCase();if(!v)return;geo=v;e.target.value=v;if(state.iframe)state.iframe.src=src();updateStatus();});
var wIn=chip.querySelector("#_asw");
if(wIn)wIn.addEventListener("input",function(e){var v=parseInt(e.target.value,10);if(v>=80){state.w=v;applySize();}});
var hIn=chip.querySelector("#_ash");
if(hIn)hIn.addEventListener("input",function(e){var v=parseInt(e.target.value,10);if(v>=80){state.h=v;applySize();}});
var desk=chip.querySelector("#_asdesk");
if(desk)desk.addEventListener("click",function(){state.w=PRESET_DESKTOP.w;state.h=PRESET_DESKTOP.h;applySize();renderChip();});
var mob=chip.querySelector("#_asmob");
if(mob)mob.addEventListener("click",function(){state.w=PRESET_MOBILE.w;state.h=PRESET_MOBILE.h;applySize();renderChip();});
var fit=chip.querySelector("#_asfit");
if(fit)fit.addEventListener("click",function(){if(state.target){var w=state.target.offsetWidth||state.target.getBoundingClientRect().width||640;state.w=Math.round(w);applySize();renderChip();}});
var pick=chip.querySelector("#_aspick");
if(pick)pick.addEventListener("click",function(){restoreTarget();startPicking();});
var close=chip.querySelector("#_asclose");
if(close)close.addEventListener("click",teardown);
updateStatus();
};
var applySize=function(){
if(!state.iframe)return;
state.iframe.style.width=state.w+"px";
state.iframe.style.height=state.h+"px";
};
var updateStatus=function(){
/* No campaign preview here — the bookmarklet doesn't know which
   campaign Ghostface will resolve for a given geo, and pretending
   to (via a baked-in label map) silently lied whenever rotation
   changed live. Just confirm the active geo. */
var opt=null;
for(var i=0;i<GEOS.length;i++){if(GEOS[i].c===geo){opt=GEOS[i];break;}}
var label=opt?opt.l:"Custom code";
var statusEl=chip.querySelector("#_asstatus");
if(statusEl)statusEl.innerHTML="<span style='width:8px;height:8px;border-radius:50%;background:#4dbd94;box-shadow:0 0 8px rgba(77,189,148,0.8);'></span><span style='color:rgba(255,255,255,0.85);'>Viewer geo: "+geo+" — "+label+"</span>";
};
var onMove=function(e){
var el=document.elementFromPoint(e.clientX,e.clientY);
if(!el||el===state.hovered)return;
if(chip.contains(el))return;
state.hovered=el;
var r=el.getBoundingClientRect();
state.highlight.style.cssText="position:fixed;pointer-events:none;border:2px dashed #4dbd94;background:rgba(77,189,148,0.12);z-index:2147483646;box-sizing:border-box;top:"+r.top+"px;left:"+r.left+"px;width:"+r.width+"px;height:"+r.height+"px;";
};
var onPickClick=function(e){
if(!state.hovered)return;
if(state.hovered===document.body||state.hovered===document.documentElement)return;
if(chip.contains(state.hovered))return;
if(!state.hovered.parentNode)return;
e.preventDefault();
e.stopPropagation();
var target=state.hovered;
stopPicking();
placeIframe(target);
renderChip();
};
var onKey=function(e){
if(e.key==="Escape"){
stopPicking();
if(!state.iframe){teardown();}else{renderChip();}
}
};
var startPicking=function(){
if(state.picking)return;
state.picking=true;
state.hovered=null;
state.highlight=document.createElement("div");
state.highlight.setAttribute("data-allstar-demo","");
state.highlight.style.cssText="position:fixed;pointer-events:none;border:2px dashed #4dbd94;background:rgba(77,189,148,0.12);z-index:2147483646;box-sizing:border-box;top:-9999px;left:-9999px;width:0;height:0;";
document.body.appendChild(state.highlight);
document.addEventListener("mousemove",onMove,true);
document.addEventListener("click",onPickClick,true);
document.addEventListener("keydown",onKey,true);
renderChip();
};
var stopPicking=function(){
if(!state.picking)return;
state.picking=false;
if(state.highlight){state.highlight.remove();state.highlight=null;}
document.removeEventListener("mousemove",onMove,true);
document.removeEventListener("click",onPickClick,true);
document.removeEventListener("keydown",onKey,true);
};
var placeIframe=function(target){
/* Default to the desktop preset on placement; user can flip to the
   mobile preset or "Reset to element width" from the floating chip
   afterwards. Previously snapped to the picked element's width with
   a 600px height — replaced with a real HLTV embed size so the demo
   reads as the production sliver out of the gate. */
state.w=PRESET_DESKTOP.w;
state.h=PRESET_DESKTOP.h;
state.iframe=document.createElement("iframe");
state.iframe.setAttribute("data-allstar-demo","");
state.iframe.src=src();
state.iframe.title="Allstar branded playlist";
state.iframe.allow="autoplay; fullscreen";
/* margin:24px 0 gives vertical breathing room so the iframe doesn't
   crowd the surrounding host-page content. The picked element stays
   in flow above; whatever sits below shifts down by iframe height +
   margin. Demo reads as "Allstar slotted between sections" instead
   of "Allstar replaced this thing". */
state.iframe.style.cssText="width:"+state.w+"px;height:"+state.h+"px;border:0;background:#000;display:block;margin:24px 0;box-shadow:0 18px 48px rgba(0,0,0,0.4);";
/* Insert immediately after the target. Using insertBefore with
   nextSibling is the standard "insertAfter" idiom — works even when
   target is the last child (nextSibling is null and insertBefore
   appends). */
target.parentNode.insertBefore(state.iframe,target.nextSibling);
state.target=target;
};
var restoreTarget=function(){
/* Just remove the iframe — we never modified the picked element, so
   there's nothing on the host page to restore. */
if(state.iframe){state.iframe.remove();state.iframe=null;}
state.target=null;
};
/* Re-render chip on viewport resize so compact/full mode toggles when
   the user opens devtools, rotates a tablet, or flips device emulation
   in DevTools — without this the chip stays at whatever width it was
   first rendered at and overflows the emulated viewport. */
var onResize=function(){renderChip();};
W.addEventListener("resize",onResize);
var teardown=function(){
stopPicking();
restoreTarget();
chip.remove();
W.removeEventListener("resize",onResize);
W.__allstarHLTVDemo=null;
};
W.__allstarHLTVDemo={teardown:teardown};
startPicking();
})();`
    .replace(/\n/g, "")
    .replace(/\s+/g, " ")
    .trim();
};
